import { ChevronLeft, ChevronRight, Eye, Plus } from 'lucide-react'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import FillingImg from '../assets/new-filling.svg'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import FilingModal from '../components/FilingModal'
import FilingProgressChart from '../components/FilingProgressChart'
import HmrcAuthModal from '../components/HmrcAuthModal'
import { useUserProfile } from '../context/UserProfileContext'
import { ct600Service } from '../utils/ct600Service'
import { companyService } from '../utils/companyService'
import { prepareHmrcAuth, getFilingRoute } from '../utils/hmrcAuthHelper'
import { isHmrcAuthorized } from '../utils/hmrcTokenService'
import { initializeTawkWithUser } from '../utils/tawkService'
import { formatUkDate } from '../utils/formatters'

interface DashboardStats {
  totalFilings: number
  draftCount: number
  submittedCount: number
  acceptedCount: number
  newCount: number
  rejectedCount: number
}

interface DeadlineItem {
  id: string
  companyName: string
  yearEnding: string
  ct600DueDate: string
  accountsDueDate: string
  daysLeft: number
  ct600Filed: boolean
  accountsFiled: boolean
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const [recentFilings, setRecentFilings] = useState<any[]>([])
  const [allForms, setAllForms] = useState<any[]>([])
  const [companies, setCompanies] = useState<any[]>([])
  const [stats, setStats] = useState<DashboardStats>({
    totalFilings: 0,
    draftCount: 0,
    submittedCount: 0,
    acceptedCount: 0,
    newCount: 0,
    rejectedCount: 0,
  })
  const [isLoadingFilings, setIsLoadingFilings] = useState(true)
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true)
  const [isFilingModalOpen, setIsFilingModalOpen] = useState(false)
  const [isHmrcAuthModalOpen, setIsHmrcAuthModalOpen] = useState(false)
  const [hmrcAuthUrl, setHmrcAuthUrl] = useState<string | null>(null)
  const [isRequestingHmrcAuth, setIsRequestingHmrcAuth] = useState(false)
  const [selectedFilingType, setSelectedFilingType] = useState<string>('')
  const [deadlineScrollIndex, setDeadlineScrollIndex] = useState(0)
  const deadlineContainerRef = useRef<HTMLDivElement>(null)
  const { user } = useUserProfile()

  useEffect(() => {
    fetchDashboardData()
  }, [])

  // Initialize Tawk.to with user information
  useEffect(() => {
    if (user) {
      initializeTawkWithUser({
        name: `${user.firstName} ${user.lastName}`.trim(),
        email: user.email,
        userId: (user as any)._id,
      })
    }
  }, [user])

  const fetchDashboardData = async () => {
    await Promise.all([fetchRecentFilings(), fetchCompanies()])
  }

  const fetchRecentFilings = async () => {
    try {
      setIsLoadingFilings(true)
      const response = await ct600Service.listForms({ page: 1, limit: 100 })

      if (response.statusCode === 200 && response.response) {
        const data: any = response.response
        let formsArray: any[] = []

        if (data.success && data.data) {
          formsArray = Array.isArray(data.data) ? data.data : []
        } else if (Array.isArray(data)) {
          formsArray = data
        }

        // Calculate stats
        const statsCalc: DashboardStats = {
          totalFilings: formsArray.length,
          draftCount: formsArray.filter(f => f.status === 'draft').length,
          submittedCount: formsArray.filter(f => f.status === 'submitted').length,
          acceptedCount: formsArray.filter(f => f.status === 'accepted').length,
          newCount: formsArray.filter(f => f.status === 'new').length,
          rejectedCount: formsArray.filter(f => f.status === 'rejected').length,
        }
        setStats(statsCalc)

        // Transform for table
        const transformed = formsArray.slice(0, 5).map((form: any) => ({
          id: form._id,
          type: 'CT600',
          entity:
            form.companyName ||
            form.sections?.companyInformation?.details?.companyName ||
            form.companyId?.name ||
            'N/A',
          period: form.accountingPeriodStart
            ? new Date(form.accountingPeriodStart).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'N/A',
          dueDate: form.accountingPeriodEnd
            ? new Date(form.accountingPeriodEnd).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'N/A',
          status: (form.status || 'draft') as
            | 'draft'
            | 'new'
            | 'submitted'
            | 'accepted'
            | 'rejected',
        }))

        setRecentFilings(transformed)
        setAllForms(formsArray)
      }
    } catch (err) {
      console.error('Error fetching filings:', err)
    } finally {
      setIsLoadingFilings(false)
    }
  }

  const fetchCompanies = async () => {
    try {
      setIsLoadingCompanies(true)
      const response = await companyService.getAllCompanies()

      if (response.statusCode === 200 && response.response) {
        const data: any = response.response
        let companiesArray: any[] = []

        if (data.success && data.data) {
          companiesArray = Array.isArray(data.data) ? data.data : []
        } else if (Array.isArray(data)) {
          companiesArray = data
        }

        setCompanies(companiesArray)
      }
    } catch (err) {
      console.error('Error fetching companies:', err)
    } finally {
      setIsLoadingCompanies(false)
    }
  }

  // CT600 + accounts share a status today (filed together); when independent
  // accounts filing lands, split these.
  const isFiledStatus = (status?: string) =>
    status === 'submitted' || status === 'accepted'

  const deadlines = useMemo<DeadlineItem[]>(() => {
    const getCompanyId = (form: any): string | undefined => {
      if (!form?.companyId) return undefined
      return typeof form.companyId === 'object'
        ? form.companyId._id
        : form.companyId
    }

    const formsByCompany = new Map<string, any[]>()
    for (const form of allForms) {
      const cid = getCompanyId(form)
      if (!cid) continue
      const list = formsByCompany.get(cid) ?? []
      list.push(form)
      formsByCompany.set(cid, list)
    }

    return companies
      .filter(c => c.accountingPeriod?.endDate || c.accountingPeriod?.end)
      .map(company => {
        const endDate =
          company.accountingPeriod?.endDate || company.accountingPeriod?.end
        const periodEnd = new Date(endDate)

        // HMRC CT600: 12 months after the accounting period ends. Backend
        // should eventually surface the authoritative HMRC-pulled date.
        const ct600Due =
          company.hmrcCt600DueDate || company.accountingPeriod?.ct600Due
        const hmrcDeadline = ct600Due
          ? new Date(ct600Due)
          : (() => {
              const d = new Date(periodEnd)
              d.setMonth(d.getMonth() + 12)
              return d
            })()

        // Companies House accounts: pulled from accountingPeriod.nextDue when
        // available, otherwise fall back to 9 months after period end.
        const chNextDue = company.accountingPeriod?.nextDue
        const companiesHouseDeadline = chNextDue
          ? new Date(chNextDue)
          : (() => {
              const d = new Date(periodEnd)
              d.setMonth(d.getMonth() + 9)
              return d
            })()

        const earliestDeadline =
          companiesHouseDeadline < hmrcDeadline
            ? companiesHouseDeadline
            : hmrcDeadline
        const diffTime = earliestDeadline.getTime() - Date.now()
        const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        const periodKey = endDate?.slice(0, 10)
        const companyForms = formsByCompany.get(company._id) ?? []
        const matchingForm =
          companyForms.find(
            f => String(f.accountingPeriodEnd ?? '').slice(0, 10) === periodKey,
          ) ?? companyForms[0]

        const ct600Filed = isFiledStatus(matchingForm?.status)
        const accountsFiled = ct600Filed

        const isoOf = (d: Date) => d.toISOString().slice(0, 10)

        return {
          id: company._id,
          companyName: company.name || company.companyName || 'N/A',
          yearEnding: `Year Ending ${periodEnd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
          ct600DueDate: formatUkDate(isoOf(hmrcDeadline)),
          accountsDueDate: formatUkDate(isoOf(companiesHouseDeadline)),
          daysLeft: Math.max(0, daysLeft),
          ct600Filed,
          accountsFiled,
        }
      })
      .slice(0, 6)
  }, [companies, allForms])

  const handleViewFiling = (record: any) => {
    navigate(`/ct600-filing/${record.id}`)
  }

  const handleFilingOptionSelect = async (option: string) => {
    setIsFilingModalOpen(false)
    setSelectedFilingType(option)

    if (option === 'confirmation-statement-filing') {
      navigate('/companies-house-filing')
      return
    }

    if (option === 'self-assessment-sa' || option === 'ct600-filing') {
      const route = getFilingRoute(option)
      if (route) {
        navigate(route)
      }
      return
    }

    if (
      option === 'self-assessment' ||
      option === 'self-assessment-trad'
    ) {
      const authorized = await isHmrcAuthorized()
      if (authorized) {
        const route = getFilingRoute(option)
        if (route) {
          navigate(route)
        }
        return
      }

      setIsRequestingHmrcAuth(true)
      setIsHmrcAuthModalOpen(true)

      try {
        const result = await prepareHmrcAuth(option)
        if (result?.authUrl) {
          setHmrcAuthUrl(result.authUrl)
        }
      } catch (error) {
        console.error('Failed to prepare HMRC auth:', error)
        setIsHmrcAuthModalOpen(false)
      } finally {
        setIsRequestingHmrcAuth(false)
      }
    }
  }

  const handleRequestHmrcAuth = async () => {
    if (!selectedFilingType || isRequestingHmrcAuth) return

    setIsRequestingHmrcAuth(true)
    try {
      const result = await prepareHmrcAuth(selectedFilingType)
      if (result?.authUrl) {
        setHmrcAuthUrl(result.authUrl)
      }
    } catch (error) {
      console.error('Error preparing HMRC auth:', error)
    } finally {
      setIsRequestingHmrcAuth(false)
    }
  }

  const scrollDeadlines = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      setDeadlineScrollIndex(prev => Math.max(0, prev - 1))
    } else {
      setDeadlineScrollIndex(prev =>
        Math.min(Math.max(0, deadlines.length - 3), prev + 1)
      )
    }
  }

  // Build chart data from stats
  const chartData = [
    { name: 'Submitted', value: stats.submittedCount || 0, color: '#374151' },
    { name: 'New', value: stats.newCount || 0, color: '#F87171' },
    { name: 'In-view', value: stats.draftCount || 0, color: '#A78BFA' },
    { name: 'Accepted', value: stats.acceptedCount || 0, color: '#34D399' },
  ]

  // Status badge styles
  const statusStyles: Record<string, { bg: string; text: string }> = {
    submitted: { bg: 'bg-[#ECFDF3]', text: 'text-[#12B76A]' },
    new: { bg: 'bg-[#E9EAEB]', text: 'text-[#181D27]' },
    draft: { bg: 'bg-[#FEF0C7]', text: 'text-[#FB6514]' },
    'in-review': { bg: 'bg-blue-50', text: 'text-blue-600' },
    accepted: { bg: 'bg-[#ECFDF3]', text: 'text-[#12B76A]' },
    rejected: { bg: 'bg-red-50', text: 'text-red-600' },
  }

  const visibleDeadlines = deadlines.slice(
    deadlineScrollIndex,
    deadlineScrollIndex + 3
  )

  return (
    <AuthenticatedLayout activeTab='dashboard' showWelcome>
      <div className='space-y-6'>
        {/* Row 1: Add Company + Upcoming Deadlines */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-6'>
          {/* Add Company Card */}
          <div className='lg:col-span-3'>
            <div className='bg-white rounded-[15px] p-5 h-full flex flex-col'>
              <div className='flex items-center gap-2.5 mb-2'>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  className='flex-shrink-0'
                >
                  <path
                    d='M22 22H2M3 22V5C3 3.34 3.84 2.5 5.5 2.5H13.5C15.16 2.5 16 3.34 16 5V22M21 22V12C21 10.34 20.16 9.5 18.5 9.5H16M7 7H12M7 11H12M7 15H12'
                    stroke='#181D27'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <span className='text-base font-bold text-[#181D27]'>
                  Add Company
                </span>
              </div>

              <div className='flex-1 flex items-center justify-center my-2'>
                <img
                  src={FillingImg}
                  alt='Add Company'
                  className='max-w-[160px] max-h-[120px] object-contain'
                />
              </div>

              <div className='flex items-end justify-between gap-4 mt-auto'>
                <p className='text-base text-[#717680] leading-6 max-w-[183px]'>
                  Register a company to start filing.
                </p>
                <button
                  onClick={() => navigate('/add-company')}
                  className='flex-shrink-0 w-[50px] h-[50px] bg-[#F04438] hover:bg-[#DC2626] text-white rounded-full flex items-center justify-center cursor-pointer transition-colors'
                  aria-label='Add Company'
                >
                  <Plus className='w-6 h-6' />
                </button>
              </div>
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className='lg:col-span-9'>
            <div className='bg-white rounded-[15px] p-6 h-full'>
              <div className='flex items-center gap-2 mb-5'>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  className='flex-shrink-0'
                >
                  <path
                    d='M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z'
                    stroke='#181D27'
                    strokeWidth='1.5'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <h2 className='text-xl font-semibold text-[#181D27]'>
                  Upcoming Deadlines
                </h2>
              </div>

              {isLoadingCompanies ? (
                <div className='flex items-center justify-center py-12'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
                </div>
              ) : deadlines.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-12 text-center'>
                  <p className='text-[#717680] text-base mb-4'>
                    No upcoming deadlines
                  </p>
                  <button
                    onClick={() => navigate('/add-company')}
                    className='text-[#F04438] hover:text-[#DC2626] text-sm font-semibold cursor-pointer'
                  >
                    Add a company to see deadlines
                  </button>
                </div>
              ) : (
                <div className='relative'>
                  {/* Left Arrow */}
                  {deadlineScrollIndex > 0 && (
                    <button
                      onClick={() => scrollDeadlines('left')}
                      className='absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-8 h-8 bg-[#F04438] rounded-full flex items-center justify-center text-white shadow-md cursor-pointer hover:bg-[#DC2626] transition-colors'
                    >
                      <ChevronLeft className='w-5 h-5' />
                    </button>
                  )}

                  {/* Deadline Cards */}
                  <div
                    ref={deadlineContainerRef}
                    className='flex flex-col md:flex-row gap-[23px]'
                  >
                    {visibleDeadlines.map(deadline => (
                      <div
                        key={deadline.id}
                        className='border border-[#E9EAEB] rounded-[7.5px] bg-[#FAFAFA] p-4 flex flex-col md:flex-1 md:basis-0 md:min-w-0'
                      >
                        <div className='flex items-start justify-end gap-2 mb-2'>
                          <span className='text-xs text-[#F04438] whitespace-nowrap flex-shrink-0'>
                            {deadline.daysLeft} Days Left
                          </span>
                        </div>

                        <div className='flex-1 mb-4'>
                          <p className='text-base font-bold text-[#181D27] mb-3'>
                            {deadline.companyName} -- {deadline.yearEnding}
                          </p>

                          <div className='space-y-2'>
                            <FilingCheck
                              label='CT600'
                              checked={deadline.ct600Filed}
                              dueDate={deadline.ct600DueDate}
                            />
                            <FilingCheck
                              label='Accounts'
                              checked={deadline.accountsFiled}
                              dueDate={deadline.accountsDueDate}
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => navigate('/ct600-filing')}
                          className='w-full h-10 bg-[#FEF3F2] border border-[#FDA29B] rounded-lg text-sm font-semibold text-[#F04438] hover:bg-[#FEE4E2] transition-colors cursor-pointer'
                          style={{
                            boxShadow:
                              '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
                          }}
                        >
                          Start Filing
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Right Arrow */}
                  {deadlineScrollIndex < deadlines.length - 3 &&
                    deadlines.length > 3 && (
                      <button
                        onClick={() => scrollDeadlines('right')}
                        className='absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-8 h-8 bg-[#F04438] rounded-full flex items-center justify-center text-white shadow-md cursor-pointer hover:bg-[#DC2626] transition-colors'
                      >
                        <ChevronRight className='w-5 h-5' />
                      </button>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Filing Progress + Filings Table */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-6'>
          {/* Filing Progress */}
          <div className='lg:col-span-3'>
            <div className='bg-[#FCFCFD] rounded-[15px] p-6 h-full flex flex-col'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-2xl font-bold text-[#181D27]'>
                  Filing Progress
                </h2>
                <button
                  onClick={() => navigate('/history')}
                  className='text-sm font-semibold text-[#F04438] hover:text-[#DC2626] px-4 py-2.5 rounded-lg cursor-pointer'
                >
                  View All
                </button>
              </div>

              {isLoadingFilings ? (
                <div className='flex items-center justify-center py-12'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
                </div>
              ) : stats.totalFilings === 0 ? (
                <div className='flex-1 flex flex-col items-center justify-center py-4'>
                  <div className='relative w-[240px] h-[240px] mb-6'>
                    <svg
                      className='w-full h-full transform -rotate-90'
                      viewBox='0 0 240 240'
                    >
                      <circle
                        cx='120'
                        cy='120'
                        r='100'
                        fill='none'
                        stroke='#F3F4F6'
                        strokeWidth='16'
                        className='opacity-30'
                      />
                    </svg>
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <div className='text-center'>
                        <div className='text-4xl font-bold text-gray-200'>
                          0%
                        </div>
                        <div className='text-sm text-gray-400'>Accepted</div>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className='flex flex-wrap justify-center gap-3 text-sm text-[#414651]'>
                    <div className='flex items-center gap-1'>
                      <div className='w-2.5 h-2.5 rounded-full bg-[#374151]'></div>
                      <span>Submitted</span>
                    </div>
                    <div className='flex items-center gap-1'>
                      <div className='w-2.5 h-2.5 rounded-full bg-[#F87171]'></div>
                      <span>New</span>
                    </div>
                    <div className='flex items-center gap-1'>
                      <div className='w-2.5 h-2.5 rounded-full bg-[#A78BFA]'></div>
                      <span>In-view</span>
                    </div>
                    <div className='flex items-center gap-1'>
                      <div className='w-2.5 h-2.5 rounded-full bg-[#34D399]'></div>
                      <span>Accepted</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='flex-1 flex flex-col'>
                  <FilingProgressChart
                    data={chartData.some(d => d.value > 0) ? chartData : chartData}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Filings Table */}
          <div className='lg:col-span-9'>
            <div className='bg-[#FCFCFD] rounded-[15px] p-6 h-full flex flex-col'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-2xl font-bold text-[#181D27]'>Filings</h2>
                <button
                  onClick={() => navigate('/history')}
                  className='px-4 py-2.5 bg-[#E9EAEB] border border-[#D5D7DA] rounded-lg text-sm font-semibold text-[#181D27] hover:bg-[#D5D7DA] transition-colors cursor-pointer'
                  style={{
                    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
                  }}
                >
                  View All
                </button>
              </div>

              {isLoadingFilings ? (
                <div className='flex items-center justify-center py-12'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
                </div>
              ) : recentFilings.length === 0 ? (
                <div className='flex-1 flex flex-col items-center justify-center py-12'>
                  <p className='text-[#717680] text-base mb-6'>
                    No filings yet
                  </p>
                  <button
                    onClick={() => setIsFilingModalOpen(true)}
                    className='bg-[#FEF3F2] border border-[#FDA29B] text-[#F04438] hover:bg-[#FEE4E2] rounded-lg px-8 h-10 text-sm font-semibold cursor-pointer transition-colors'
                  >
                    Start Filing
                  </button>
                </div>
              ) : (
                <div className='flex-1 min-h-0'>
                  {/* Mobile: Card View */}
                  <div className='md:hidden space-y-3'>
                    {recentFilings.map(record => (
                      <div
                        key={record.id}
                        className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'
                      >
                        <div className='flex items-start justify-between mb-3'>
                          <div>
                            <p className='text-sm font-semibold text-[#181D27]'>{record.type}</p>
                            <p className='text-sm text-[#717680] mt-0.5'>{record.entity}</p>
                          </div>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              statusStyles[record.status]?.bg || 'bg-gray-100'
                            } ${statusStyles[record.status]?.text || 'text-gray-700'}`}
                          >
                            {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                          </span>
                        </div>
                        <div className='flex items-center justify-between text-xs text-[#717680]'>
                          <div className='flex items-center gap-4'>
                            <span>Period: <span className='text-[#181D27] font-medium'>{record.period}</span></span>
                            <span>Due: <span className='text-[#181D27] font-medium'>{record.dueDate}</span></span>
                          </div>
                          <button
                            onClick={() => handleViewFiling(record)}
                            className='text-[#717680] hover:text-[#181D27] transition-colors cursor-pointer'
                          >
                            <Eye className='w-5 h-5' />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: Table View */}
                  <div className='hidden md:block overflow-x-auto'>
                    <div className='bg-[#F5F5F5] rounded-lg p-2 pb-4'>
                      <table
                        className='w-full min-w-[760px]'
                        style={{
                          tableLayout: 'fixed',
                          borderCollapse: 'separate',
                          borderSpacing: '0 8px',
                        }}
                      >
                        <colgroup>
                          <col style={{ width: '100px' }} />
                          <col />
                          <col style={{ width: '130px' }} />
                          <col style={{ width: '130px' }} />
                          <col style={{ width: '110px' }} />
                          <col style={{ width: '48px' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className='px-3 py-2 text-left text-sm font-semibold text-[#232323]'>
                              <span className='inline-flex items-center gap-1'>
                                Type
                                <SortIcon />
                              </span>
                            </th>
                            <th className='px-3 py-2 text-left text-sm font-semibold text-[#232323]'>
                              <span className='inline-flex items-center gap-1'>
                                Entity
                                <SortIcon />
                              </span>
                            </th>
                            <th className='px-3 py-2 text-left text-sm font-semibold text-[#232323]'>
                              <span className='inline-flex items-center gap-1'>
                                Period
                                <SortIcon />
                              </span>
                            </th>
                            <th className='px-3 py-2 text-left text-sm font-semibold text-[#232323]'>
                              <span className='inline-flex items-center gap-1'>
                                Due Date
                                <SortIcon />
                              </span>
                            </th>
                            <th className='px-3 py-2 text-left text-sm font-semibold text-[#232323]'>
                              <span className='inline-flex items-center gap-1'>
                                Status
                                <SortIcon />
                              </span>
                            </th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentFilings.map(record => (
                            <tr
                              key={record.id}
                              className='bg-white'
                              style={{ height: '56px' }}
                            >
                              <td className='px-3 py-3 text-left text-sm text-[#181D27] rounded-l-lg'>
                                {record.type}
                              </td>
                              <td
                                className='px-3 py-3 text-left text-sm text-[#181D27] truncate'
                                title={record.entity}
                              >
                                {record.entity}
                              </td>
                              <td className='px-3 py-3 text-left text-sm text-[#181D27]'>
                                {record.period}
                              </td>
                              <td className='px-3 py-3 text-left text-sm text-[#181D27]'>
                                {record.dueDate}
                              </td>
                              <td className='px-3 py-3 text-left'>
                                <span
                                  className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs w-[84px] ${
                                    statusStyles[record.status]?.bg ||
                                    'bg-gray-100'
                                  } ${statusStyles[record.status]?.text || 'text-gray-700'}`}
                                >
                                  {record.status.charAt(0).toUpperCase() +
                                    record.status.slice(1)}
                                </span>
                              </td>
                              <td className='px-2 py-3 text-left rounded-r-lg'>
                                <button
                                  onClick={() => handleViewFiling(record)}
                                  className='text-[#717680] hover:text-[#181D27] transition-colors cursor-pointer'
                                >
                                  <Eye className='w-5 h-5' />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Global Modals */}
      <FilingModal
        isOpen={isFilingModalOpen}
        onClose={() => setIsFilingModalOpen(false)}
        onSelectOption={handleFilingOptionSelect}
        userEmail={user?.email}
      />
      <HmrcAuthModal
        isOpen={isHmrcAuthModalOpen}
        onClose={() => setIsHmrcAuthModalOpen(false)}
        authUrl={hmrcAuthUrl}
        isRequestingAuth={isRequestingHmrcAuth}
        onAuthorize={handleRequestHmrcAuth}
        filingType={selectedFilingType}
      />
    </AuthenticatedLayout>
  )
}

function FilingCheck({
  label,
  checked,
  dueDate,
}: {
  label: string
  checked: boolean
  dueDate?: string
}) {
  return (
    <div className='flex items-center gap-2 text-xs'>
      <span
        className={`flex-shrink-0 flex items-center justify-center w-4 h-4 rounded border ${
          checked
            ? 'bg-[#12B76A] border-[#12B76A]'
            : 'bg-white border-[#D5D7DA]'
        }`}
      >
        {checked && (
          <svg
            width='10'
            height='10'
            viewBox='0 0 10 10'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M8.333 2.5L3.75 7.083 1.667 5'
              stroke='white'
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
      </span>
      <span className='text-[#181D27] font-medium'>{label}</span>
      {dueDate && (
        <span className='text-[#717680]'>
          – Due date:{' '}
          <span className='text-[#181D27]'>{dueDate}</span>
        </span>
      )}
    </div>
  )
}

/** Sort icon matching the Figma design */
function SortIcon() {
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      className='flex-shrink-0 opacity-50'
    >
      <path d='M6 2L8 5H4L6 2Z' fill='#717680' />
      <path d='M6 10L4 7H8L6 10Z' fill='#717680' />
    </svg>
  )
}
